import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ModalShell from '../../components/ModalShell';
import HASpecPicker from '../../components/HASpecPicker';
import CrossBranchStockView from './CrossBranchStockView';
import MasterLibraryBrowseModal from './MasterLibraryBrowseModal';
import VendorAutocomplete from '../../components/VendorAutocomplete';
import { COLOR_SWATCHES } from '../../lib/haSpecs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Keep in sync with `FormFactor` / `TechTier` in `backend/models_ha.py`.
// `POCKET` is the enum value; the UI shows it as "Pocket Aids" for clarity.
const FORM_FACTORS = ['RIC', 'BTE', 'ITE', 'ITC', 'CIC', 'IIC', 'POCKET', 'accessory'];
const FORM_FACTOR_LABELS = {
  RIC: 'RIC', BTE: 'BTE', ITE: 'ITE', ITC: 'ITC', CIC: 'CIC', IIC: 'IIC',
  POCKET: 'Pocket Aids', accessory: 'Accessory',
};
const TECH_TIERS = ['basic', 'essential', 'standard', 'advanced', 'premium'];

// Form-factors that carry a physical colour / power / receiver-length
// spec. Accessories and "POCKET" aids don't get the spec picker.
const HA_FORM_FACTOR_SET = new Set(['RIC', 'BTE', 'ITE', 'ITC', 'CIC', 'IIC']);

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function ProductCataloguePage() {
  const [rows, setRows] = useState([]);
  const [specSummary, setSpecSummary] = useState({});
  const [search, setSearch] = useState('');
  const [formFactor, setFormFactor] = useState('');
  const [showForm, setShowForm] = useState(null); // null | 'new' | product_id
  const [showLibrary, setShowLibrary] = useState(false);
  const [tab, setTab] = useState('catalogue'); // 'catalogue' | 'availability'

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (formFactor) params.form_factor = formFactor;
    const r = await axios.get(`${API}/ha/products`, { params });
    // Guard: /ha/products is offline-cacheable — a non-array fallback
    // (Cloudflare 502 HTML / stale-decrypt cache miss) would crash rows.map.
    setRows(Array.isArray(r.data) ? r.data : []);
    // Live in-stock summary drives the per-row colour/power/size chips.
    // Non-fatal if it 500s — chips just don't render.
    try {
      const s = await axios.get(`${API}/ha/products/spec-summary`);
      setSpecSummary(s.data && typeof s.data === 'object' ? s.data : {});
    } catch {
      setSpecSummary({});
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, formFactor]);

  return (
    <div className="p-5" data-testid="ha-products-page">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Product Catalogue</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">Brand / model master. Inventory lives per branch on SerialItem & AccessoryStock.</p>
        </div>
        {tab === 'catalogue' && (
          <div className="flex items-center gap-2">
            <button
              data-testid="ha-product-add-from-library"
              onClick={() => setShowLibrary(true)}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 rounded-md shadow-sm"
              title="Browse the Audinexa Master Library and add SKUs to your catalogue in one click"
            >+ Add from Library</button>
            <button
              data-testid="ha-product-new"
              onClick={() => setShowForm('new')}
              className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md shadow-sm"
            >+ New Product</button>
          </div>
        )}
      </div>

      {/* Inline tabs — Catalogue vs Cross-Branch Availability. */}
      <div className="mb-4 border-b border-slate-200 flex items-center gap-1" data-testid="ha-products-tabs">
        {[
          { key: 'catalogue',    label: 'Catalogue' },
          { key: 'availability', label: 'Cross-Branch Availability' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`ha-products-tab-${t.key}`}
            className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider border-b-2 -mb-[1px] transition-colors ${
              tab === t.key
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'availability' ? (
        <CrossBranchStockView embedded testidPrefix="cbs-catalogue" />
      ) : (
        <CatalogueBody
          rows={rows}
          specSummary={specSummary}
          search={search}
          setSearch={setSearch}
          formFactor={formFactor}
          setFormFactor={setFormFactor}
          setShowForm={setShowForm}
        />
      )}

      {showForm && (
        <ProductForm
          productId={showForm === 'new' ? null : showForm}
          onClose={() => setShowForm(null)}
          onSaved={() => { setShowForm(null); load(); }}
        />
      )}

      {showLibrary && (
        <MasterLibraryBrowseModal
          onClose={() => { setShowLibrary(false); load(); }}
          onAdopted={() => load()}
        />
      )}
    </div>
  );
}

// Extracted body so the tab toggle at the top of the page can swap between
// the catalogue table and the CrossBranchStockView cleanly.
function CatalogueBody({ rows, specSummary, search, setSearch, formFactor, setFormFactor, setShowForm }) {
  return (
    <>
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          placeholder="Search brand / model…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="ha-products-search"
          className="flex-1 min-w-[180px] max-w-md bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm"
        />
        <select
          value={formFactor}
          onChange={(e) => setFormFactor(e.target.value)}
          data-testid="ha-products-form-factor-filter"
          className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
        >
          <option value="">All form factors</option>
          {FORM_FACTORS.map(f => <option key={f} value={f}>{FORM_FACTOR_LABELS[f] || f}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Brand</th>
              <th className="px-3 py-2 text-left">Model · Live spec in stock</th>
              <th className="px-3 py-2 text-left">Form</th>
              <th className="px-3 py-2 text-left">Tier</th>
              <th className="px-3 py-2 text-right">MRP</th>
              <th className="px-3 py-2 text-center">Sale Unit</th>
              <th className="px-3 py-2 text-right">Min Sell</th>
              <th className="px-3 py-2 text-right">Wty (m)</th>
              <th className="px-3 py-2 text-center">Serialised</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className="py-8 text-center text-slate-400 italic text-xs">No products yet. Click + New Product to add your first SKU.</td></tr>
            )}
            {rows.map(p => (
              <tr key={p.product_id} className={`border-t border-slate-100 ${!p.active ? 'opacity-50' : ''}`} data-testid={`ha-product-row-${p.product_id}`}>
                <td className="px-3 py-2 font-semibold align-top">{p.brand}</td>
                <td className="px-3 py-2 align-top">
                  <div>{p.model}</div>
                  <SpecChips summary={specSummary[p.product_id]} product={p} testid={`ha-product-chips-${p.product_id}`} />
                </td>
                <td className="px-3 py-2 text-xs align-top"><span className="bg-slate-100 rounded px-1.5 py-0.5">{FORM_FACTOR_LABELS[p.form_factor] || p.form_factor}</span></td>
                <td className="px-3 py-2 text-xs capitalize align-top">{p.tech_tier || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums align-top">{fmtINR(p.mrp)}</td>
                <td className="px-3 py-2 text-center text-[11px] align-top">
                  {p.form_factor === 'accessory' ? (
                    <span
                      className="bg-emerald-50 text-emerald-800 rounded px-1.5 py-0.5 uppercase tracking-wide"
                      data-testid={`ha-product-earside-${p.product_id}`}
                    >
                      {p.ear_side === 'right' ? 'Right' :
                       p.ear_side === 'left'  ? 'Left'  : 'Common'}
                    </span>
                  ) : (
                    <span className="bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5 uppercase tracking-wide">
                      {p.sale_unit === 'kit' ? 'Kit' : (p.sale_unit === 'pair' ? 'Pair · 2' : 'Single · 1')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-700 align-top">{fmtINR(p.min_sell_price)}</td>
                <td className="px-3 py-2 text-right tabular-nums align-top">{p.warranty_months}</td>
                <td className="px-3 py-2 text-center align-top">{p.is_serialised ? '✓' : '—'}</td>
                <td className="px-3 py-2 text-right align-top">
                  <button
                    onClick={() => setShowForm(p.product_id)}
                    data-testid={`ha-product-edit-${p.product_id}`}
                    className="text-[10px] text-indigo-600 font-semibold hover:underline">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ProductForm({ productId, onClose, onSaved }) {
  const [f, setF] = useState({
    brand: '', model: '', form_factor: 'RIC', tech_tier: '',
    warranty_months: 24, mrp: 0, cost: 0, min_sell_price: 0,
    sale_unit: 'single',
    // 2026-02-13 · Packing-mode flags. Legacy `sale_unit` is auto-derived
    // on save so downstream code (Quick-Sale, analytics) keeps working
    // — the UI only surfaces these two flags to the audiologist.
    sellable_as_single: true,
    sellable_as_kit: false,
    ear_side: null,
    hsn: '9021', gst_rate: 18, is_serialised: true, connectivity: [],
    vendor_id: '',
  });
  // Default-spec picker state — stored as the picker's native shape so
  // the widget round-trips cleanly. Flattened to default_color / _power
  // / _size in save().
  //
  // 2026-02-11 · The Catalogue New/Edit modal is now pure-SKU: the
  // Product Intent radio + Serial Numbers section that used to live
  // here were architecturally wrong (they mixed a *listing* concept
  // with a *pool intake* concept). Serials are now registered from
  // the pool tabs — Saleable Stock, Demo Stock, Accessories — via
  // <AddSerialModal/>. This form only manages the SKU shell.
  const [defaultSpec, setDefaultSpec] = useState({});
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  // Vendors list — powers the "Vendor" dropdown so a SKU can be linked
  // to the supplier that offers it. Optional field; older SKUs are
  // vendor-less until edited.
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    axios.get(`${API}/vendors`).then((r) => setVendors(Array.isArray(r.data) ? r.data : [])).catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    if (!productId) return;
    axios.get(`${API}/ha/products/${productId}`).then(r => {
      const p = r.data || {};
      setF(p);
      // Reconstruct HASpecPicker shape from the flat fields we persist.
      const dt = String(p.form_factor || '').toUpperCase();
      const isRIC = dt === 'RIC' || dt === 'RITE';
      const isBTE = dt === 'BTE';
      setDefaultSpec({
        color: p.default_color || '',
        ...(isRIC && p.default_power ? { receiver_power: p.default_power } : {}),
        ...(isBTE && p.default_power ? { bte_power: p.default_power } : {}),
        ...(isRIC && p.default_size  ? { receiver_length: p.default_size } : {}),
        ...(isBTE && p.default_size  ? { slim_tube_length: p.default_size } : {}),
      });
    });
  }, [productId]);

  const save = async () => {
    setErr('');
    const isAccessory = f.form_factor === 'accessory';
    if (!isAccessory && !f.sellable_as_single && !f.sellable_as_kit) {
      setErr('Pick at least one packing mode: Single Unit and/or Kit.');
      return;
    }
    setSaving(true);
    try {
      const ss = isAccessory ? true  : !!f.sellable_as_single;
      const sk = isAccessory ? false : !!f.sellable_as_kit;
      // Derive the legacy `sale_unit` from the two flags so downstream
      // code that still groups by it (Quick-Sale, backfill script,
      // analytics) keeps working:
      //   kit-only          → "kit"
      //   single-only OR both → "single"
      const derivedSaleUnit = isAccessory
        ? 'single'
        : ((sk && !ss) ? 'kit' : 'single');
      const body = {
        ...f,
        tech_tier: f.tech_tier || null,
        sale_unit: derivedSaleUnit,
        sellable_as_single: ss,
        sellable_as_kit: sk,
        ear_side: isAccessory ? (f.ear_side || 'common') : null,
        // Preserve the audiologist's serialised choice — Catalogue is
        // SKU-only now, so no auto-flip based on queued serials.
        is_serialised: !!f.is_serialised,
        vendor_id: f.vendor_id || null,
        // Flatten HASpecPicker shape → three catalogue fields. Empty
        // strings become null so the DB stays clean of blank markers.
        default_color: (defaultSpec.color_other || defaultSpec.color || '') || null,
        default_power:
          (defaultSpec.receiver_power || defaultSpec.bte_power || '') || null,
        default_size:
          (defaultSpec.receiver_length || defaultSpec.slim_tube_length || '') || null,
      };
      if (productId) {
        await axios.put(`${API}/ha/products/${productId}`, body);
      } else {
        await axios.post(`${API}/ha/products`, body);
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : JSON.stringify(d) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      onClose={onClose}
      cardClassName="max-w-2xl w-full p-5 max-h-[92vh] overflow-auto"
      testid="ha-product-form-modal"
    >
      <h2 className="text-lg font-bold mb-4">{productId ? 'Edit Product' : 'New Product'}</h2>
      {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-3">{err}</div>}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Brand *"><input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="ha-pf-brand" className="w-full border border-slate-300 rounded px-2 py-1" /></Field>
        <Field label="Model *"><input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} data-testid="ha-pf-model" className="w-full border border-slate-300 rounded px-2 py-1" /></Field>
        <Field label="Form Factor">
          <select
            value={f.form_factor}
            onChange={(e) => {
              const next = e.target.value;
              // Form-factor switch resets the ear-side + is_serialised
              // defaults so the modal always reflects sensible starting
              // values for the picked kind of product.
              setF((prev) => ({
                ...prev,
                form_factor: next,
                ear_side: next === 'accessory' ? (prev.ear_side || 'common') : null,
                // Accessories are not tracked by serial number by default;
                // HA form-factors are. Only auto-flip when creating a NEW
                // product (productId absent) so we don't clobber an edit.
                ...(productId ? {} : { is_serialised: next !== 'accessory' }),
              }));
            }}
            data-testid="ha-pf-formfactor"
            className="w-full border border-slate-300 rounded px-2 py-1"
          >
            {FORM_FACTORS.map(ff => <option key={ff} value={ff}>{FORM_FACTOR_LABELS[ff] || ff}</option>)}
          </select>
        </Field>
        <Field label="Tech Tier">
          <select value={f.tech_tier || ''} onChange={(e) => setF({ ...f, tech_tier: e.target.value })} data-testid="ha-pf-tier" className="w-full border border-slate-300 rounded px-2 py-1">
            <option value="">—</option>
            {TECH_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="MRP (₹)"><input type="number" value={f.mrp} onChange={(e) => setF({ ...f, mrp: Number(e.target.value) })} data-testid="ha-pf-mrp" className="w-full border border-slate-300 rounded px-2 py-1" /></Field>
        <Field label="Sale Unit">
          {/* Context-aware: HA form-factors capture manufacturer packing
              (Single / Pair / Kit); Accessories capture ear-side because
              a battery/receiver/dome is inherently side-agnostic or side-
              specific rather than "packed" in a box. Underlying field
              differs (`sale_unit` vs `ear_side`) but the label stays
              "Sale Unit" per the user's UX ask. */}
          {f.form_factor === 'accessory' ? (
            <select
              value={f.ear_side || 'common'}
              onChange={(e) => setF({ ...f, ear_side: e.target.value })}
              data-testid="ha-pf-saleunit-earside"
              className="w-full border border-slate-300 rounded px-2 py-1"
            >
              <option value="common">Common (either ear)</option>
              <option value="left">Left Ear</option>
              <option value="right">Right Ear</option>
            </select>
          ) : (
            <div
              className="border border-slate-300 rounded px-2 py-1.5 space-y-1 bg-white"
              data-testid="ha-pf-saleunit-checkboxes"
            >
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!f.sellable_as_single}
                  onChange={(e) => setF({ ...f, sellable_as_single: e.target.checked })}
                  data-testid="ha-pf-sellable-single"
                />
                <span>Sellable as <b>Single Unit</b> <span className="text-slate-500 font-normal">· 1 aid, priced per unit</span></span>
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!f.sellable_as_kit}
                  onChange={(e) => setF({ ...f, sellable_as_kit: e.target.checked })}
                  data-testid="ha-pf-sellable-kit"
                />
                <span>Sellable as <b>Kit</b> <span className="text-slate-500 font-normal">· Right + Left pair, priced per kit</span></span>
              </label>
            </div>
          )}
        </Field>
        <Field label="Min Sell Price (₹)"><input type="number" value={f.min_sell_price} onChange={(e) => setF({ ...f, min_sell_price: Number(e.target.value) })} data-testid="ha-pf-min" className="w-full border border-slate-300 rounded px-2 py-1" /></Field>
        <Field label="Warranty (months)"><input type="number" value={f.warranty_months} onChange={(e) => setF({ ...f, warranty_months: Number(e.target.value) })} data-testid="ha-pf-wty" className="w-full border border-slate-300 rounded px-2 py-1" /></Field>
        <Field label="HSN"><input value={f.hsn} onChange={(e) => setF({ ...f, hsn: e.target.value })} className="w-full border border-slate-300 rounded px-2 py-1" /></Field>
        <Field label="GST %"><input type="number" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: Number(e.target.value) })} className="w-full border border-slate-300 rounded px-2 py-1" /></Field>
        <Field label="Serialised?" className="col-span-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_serialised} onChange={(e) => setF({ ...f, is_serialised: e.target.checked })} data-testid="ha-pf-serialised" />
            <span>Track each physical unit by serial number (uncheck for accessories / batteries)</span>
          </label>
        </Field>
        <Field label="Vendor" className="col-span-2">
          <VendorAutocomplete
            vendors={vendors}
            value={f.vendor_id || ''}
            onChange={(id) => setF({ ...f, vendor_id: id || null })}
            testid="ha-pf-vendor"
            placeholder="Search vendor by name, state or GSTIN… (leave blank for unassigned)"
          />
          <div className="text-[10px] text-slate-400 mt-1 leading-tight">
            Optional · the supplier that offers this SKU. Powers vendor filters in the catalogue and pre-fills the PO flow.
          </div>
        </Field>
      </div>

      {/* ====== Default device spec — HA form-factors only ====== */}
      {HA_FORM_FACTOR_SET.has(f.form_factor) && (
        <div className="mt-4 pt-3 border-t border-slate-100" data-testid="ha-pf-default-spec-section">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
            Default Colour · Power · Size
            <span className="ml-1 lowercase font-normal italic text-slate-400">
              · optional · shown as this SKU's default when spec is missing on a serial
            </span>
          </div>
          <HASpecPicker
            deviceType={f.form_factor}
            side="L"
            compact
            value={defaultSpec}
            onChange={setDefaultSpec}
            testIdPrefix="ha-pf-spec"
          />
        </div>
      )}

      {/* SKU-only reminder — 2026-02-11 the Catalogue no longer intakes
          physical serials. Register units from Saleable Stock / Demo
          Stock / Accessories tabs. */}
      <div
        className="mt-5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600"
        data-testid="ha-pf-serial-intake-hint"
      >
        <span className="font-semibold text-slate-700">Adding physical units? </span>
        Serials are registered from the <b>Saleable Stock</b>, <b>Demo Stock</b>, or <b>Accessories</b> tab of Inventory — not from the Catalogue. Save this SKU first, then head to the right pool tab.
      </div>

      <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-slate-200">
        <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="ha-pf-save"
          className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded"
        >{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </ModalShell>
  );
}

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">{label}</span>
    {children}
  </label>
);

// ─── Per-row spec chips ─────────────────────────────────────────
// Renders three tiny stacks: colours-in-stock / powers-in-stock /
// sizes-in-stock. Values come from the /products/spec-summary endpoint
// which aggregates live IN_STOCK serial_items.spec across all branches
// of the current clinic. Falls back gracefully to catalogue defaults
// or "—" when nothing is in stock.
function SpecChips({ summary, product, testid }) {
  const colors = (summary && summary.colors) || [];
  const powers = (summary && summary.powers) || [];
  const sizes  = (summary && summary.sizes)  || [];
  const anyLive = colors.length + powers.length + sizes.length > 0;
  const anyDefault =
    product && (product.default_color || product.default_power || product.default_size);
  if (!anyLive && !anyDefault) return null;
  const chip = (val, extraCls = '') => (
    <span className={`inline-block text-[9.5px] font-semibold rounded px-1 py-0.5 mr-0.5 mb-0.5 ${extraCls || 'bg-slate-100 text-slate-700'}`}>
      {val}
    </span>
  );
  // Colour chip renders a tiny dot in the actual shell tint next to
  // the value name. `other` (custom colours) draws a dashed empty
  // circle so we don't mis-represent an unknown factory tint.
  const colorChip = (val, extraCls = '') => {
    const swatch = COLOR_SWATCHES[val];
    const dotStyle = swatch && swatch !== 'transparent'
      ? { background: swatch, border: '1px solid rgba(0,0,0,0.18)' }
      : { background: 'transparent', border: '1px dashed rgba(0,0,0,0.3)' };
    return (
      <span
        className={`inline-flex items-center gap-[3px] text-[9.5px] font-semibold rounded px-1 py-0.5 mr-0.5 mb-0.5 ${extraCls || 'bg-slate-100 text-slate-700'}`}
        data-testid={`ha-color-chip-${val}`}
      >
        <span
          aria-hidden
          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
          style={dotStyle}
        />
        {val}
      </span>
    );
  };
  return (
    <div className="mt-1 space-y-0.5" data-testid={testid}>
      {colors.length > 0 && (
        <div className="flex flex-wrap items-center">
          <span className="text-[8.5px] uppercase tracking-wider text-slate-400 mr-1 font-bold">Colours</span>
          {colors.map((c) => <React.Fragment key={c}>{colorChip(c)}</React.Fragment>)}
        </div>
      )}
      {powers.length > 0 && (
        <div className="flex flex-wrap items-center">
          <span className="text-[8.5px] uppercase tracking-wider text-slate-400 mr-1 font-bold">Powers</span>
          {powers.map((p) => <React.Fragment key={p}>{chip(p)}</React.Fragment>)}
        </div>
      )}
      {sizes.length > 0 && (
        <div className="flex flex-wrap items-center">
          <span className="text-[8.5px] uppercase tracking-wider text-slate-400 mr-1 font-bold">Sizes</span>
          {sizes.map((sz) => <React.Fragment key={sz}>{chip(sz)}</React.Fragment>)}
        </div>
      )}
      {!anyLive && anyDefault && (
        <div className="flex flex-wrap items-center">
          <span className="text-[8.5px] uppercase tracking-wider text-slate-400 mr-1 font-bold">Default</span>
          {product.default_color && colorChip(product.default_color, 'bg-indigo-50 text-indigo-700')}
          {product.default_power && chip(product.default_power, 'bg-indigo-50 text-indigo-700')}
          {product.default_size  && chip(product.default_size,  'bg-indigo-50 text-indigo-700')}
        </div>
      )}
    </div>
  );
}
